import React, { useEffect, useState, useRef, useCallback } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import InfoIcon from "../support_files/InfoIcon";
import HelpIcon from "../support_files/HelpIcon";
import ToggleButton from '@mui/material/ToggleButton';

import PublicIcon from '@mui/icons-material/Public';
import CancelIcon from '@mui/icons-material/Cancel';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import TocIcon from '@mui/icons-material/Toc';
import SourceIcon from '@mui/icons-material/Source';

import Fade from '@mui/material/Fade';

import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';

import { ResponsiveBar } from '@nivo/bar'

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import ethnicityInfoData from "../info_data/ethnicityInfoData.json"

import { createTheme, ThemeProvider } from '@mui/material/styles';

export default function EthnicityInfoDrawer({
  selectedInfo, setSelectedInfo,
}) {

  // Use ref to directly manipulate DOM classes - no re-renders
  const headerRef = useRef(null);
  const handleScroll = useCallback((event) => {
    const scrollTop = event.target.scrollTop;
    if (headerRef.current) {
      if (scrollTop > 20) {
        headerRef.current.classList.add('header-translucent');
        headerRef.current.classList.remove('header-solid');
      } else {
        headerRef.current.classList.add('header-solid');
        headerRef.current.classList.remove('header-translucent');
      }
    }
  }, []); // Empty dependency array since we're using refs

  const EthnicityBarChart = () => {
    // const [animatedData, setAnimatedData] = useState([]);
    // const [isAnimating, setIsAnimating] = useState(true);

    // // Initialize with zero values and animate to actual values
    // useEffect(() => {
    //   if (!selectedInfo) return;

    //   // Start with zero values
    //   const zeroData = ethnicityInfoData.reverse().map(item => ({
    //     ...item,
    //     percentage: 0
    //   }));
      
    //   setAnimatedData(zeroData);
      
    //   // Animate to actual values after a short delay
    //   const timer = setTimeout(() => {
    //     setAnimatedData(ethnicityInfoData.reverse());
    //     setIsAnimating(false);
    //   }, 100);
      
    //   return () => clearTimeout(timer);
    // }, [selectedInfo]);

    // Color mapping for ethnicity groups
    const getColor = (ethnicityGroup) => {
      const colorMap = {
        'Asian': '#ca7e8cff',
        'Black or African American': '#a6b987ff',
        'Latino': '#e69e60ff',
        'Middle Eastern or North African': '#62bb7eff',
        'Native American': '#ceba4bff',
        'Pacific Islander': '#6cbdaeff',
        'White': '#72a4c3ff',
        'Other': '#c5b4e3',
      };
      return colorMap[ethnicityGroup] || '#666666';
    };

    return (
      <div style={{ height: 400, width: '100%' }}>
        <ResponsiveBar
          data={ethnicityInfoData.slice().reverse()}
          keys={['percentage']}
          indexBy="ethnicity"
          layout="horizontal"
          margin={{ top: 0, right: 10, bottom: 50, left: 155 }}
          padding={0.3}
          valueScale={{ type: 'linear', min: 0, max: 30 }}
          indexScale={{ type: 'band', round: true }}
          colors={({ data }) => getColor(data.ethnicity_group)}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Percentage',
            legendPosition: 'middle',
            legendOffset: 36,
            format: value => `${value}%`,
            tickValues: [0, 10, 20, 30]
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Ethnicity',
            legendPosition: 'middle',
            legendOffset: -150,
          }}
          enableLabel={true}
          label={({ value }) => `${value.toFixed(1)}%`}
           labelOffset={10}        // Push labels away from bars (positive = right, negative = left)
labelTextAnchor="start" // Options: 'start', 'middle', 'end' 
          labelSkipWidth={4}
          // labelSkipHeight={12}
          labelTextColor="#ffffff"
          tooltip={({ indexValue, value, color, data }) => (
            <div
              style={{
                background: 'rgba(0, 0, 0, 0.8)',
                color: '#ffffff',
                fontSize: '14px',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                padding: '8px 12px'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {indexValue}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                {data.ethnicity_group}
              </div>
              <div style={{ fontWeight: '500' }}>
                {value.toFixed(2)}%
              </div>
            </div>
          )}
          theme={{
            background: 'transparent',
            text: {
              fill: '#ffffff',
              fontSize: 11
            },
            axis: {
              domain: {
                line: {
                  stroke: '#777777',
                  strokeWidth: 1
                }
              },
              legend: {
                text: {
                  fill: '#ffffff',
                  fontSize: 12
                }
              },
              ticks: {
                line: {
                  stroke: '#777777',
                  strokeWidth: 1
                },
                text: {
                  fill: '#ffffff',
                  fontSize: 11
                }
              }
            },
            grid: {
              line: {
                stroke: '#444444',
                strokeWidth: 1
              }
            }
          }}
          animate={true}
          motionConfig="gentle"
        />
      </div>
    );
  };

  const EthnicityTable = () => {
    // Function to format count with commas
    const formatCount = (count) => {
      return count.toLocaleString();
    };

    return (
      <TableContainer
        component={Paper}
        sx={{
          overflow: 'auto',
          marginBottom: '20px',
          borderRadius: '12px',
          backgroundColor: '#1a1a1a',
          '& .MuiTable-root': {
            '& .MuiTableCell-root': {
              color: 'white',
              borderBottom: '1px solid #444'
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: '#333'
            }
          }
        }}
      >
        <Table sx={{ maxWidth: 800 }} size="small" aria-label="ethnicity table">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: '#2d2d2d',
                  zIndex: 1,
                  boxShadow: '2px 0 5px rgba(0,0,0,0.1)'
                }}
              >
                Ethnicity
              </TableCell>
              {/* <TableCell align="right" sx={{ width: '200px' }}>
                Ethnicity Group
              </TableCell> */}
              <TableCell align="right" sx={{ width: '100px' }}>
                Percentage
              </TableCell>
              <TableCell align="right" sx={{ width: '120px' }}>
                Count
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ethnicityInfoData
              .reverse()
              .slice()
              .map((item) => (
              <TableRow
                key={item.ethnicity}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#2d2d2d',
                    zIndex: 1,
                    boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
                    fontWeight: 500,
                    padding: '6px 10px'
                  }}
                >
                  {item.ethnicity}
                </TableCell>
                {/* <TableCell align="right">
                  {item.ethnicity_group}
                </TableCell> */}
                <TableCell align="right">
                  {item.percentage.toFixed(2)}%
                </TableCell>
                <TableCell align="right">
                  {formatCount(item.count)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const drawerContent = () => (
    <div className="drawer-container" style={{ overflow: 'auto' }} onScroll={handleScroll}>
      <div ref={headerRef} className="sticky-header header-solid">
        <div className="header-content">
          <div className="header-left">
            <PublicIcon />
            <h2 className="header-title">Ethnicity</h2>
          </div>
          <div className="header-right">
            <button
              onClick={() => { setSelectedInfo(false) }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <CancelIcon sx={{ fill: "#b4b4b4ff", fontSize: "28px" }} />
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <InsertChartIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Summary</h3>
            </div>
            <div style={{height: '400px', marginBottom: '10px'}}>
              <EthnicityBarChart />
            </div>
            <p className="card-text">
              This chart illustrates the ethnic composition of New York City as of 2020 according to the US Census¹. Each bar represents a different ethnicity and is color-coded by broader racial categories.
            </p>
          </div>
        </div>

        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <TocIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Details</h3>
            </div>
            <div className="visual-container">
              <div style={{height: '340px', overflow: 'scroll', marginBottom: "10px" }}>
                <EthnicityTable />
              </div>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <SourceIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Source</h3>
            </div>
            <p className="card-text">
              [1] U.S. Census Bureau, 2020 Decennial Census (NYC)
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <SwipeableDrawer
        anchor="bottom"
        open={selectedInfo}
        onClose={() => setSelectedInfo(false)}
        onOpen={() => setSelectedInfo(true)}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: '#071c34ff',
            color: "white",
            borderTopRightRadius: '25px 15px',
            borderTopLeftRadius: '25px 15px',
            height: 'calc(100dvh - 95px)',
            overflow: 'auto'
          },
        }}
      >
        <div className="additionalInfoContainer">
          {drawerContent()}
        </div>
      </SwipeableDrawer>
    </div>
  )
}